/**
 * useIframeInspector — provider-agnostic iframe success/cancel/engagement
 * detector. Mirrors the RN WebViewModal pattern (URL-substring matching +
 * confirmation-code param extraction) and adds postMessage as a parallel
 * signal channel for cooperating providers.
 *
 * Source-of-truth for the rules is functions-py PROVIDER_DETECTION_PATTERNS;
 * the brain's prefill_checkout tool delivers them to the cart so this hook
 * never imports a provider name. All it does is: apply rules → emit events.
 *
 * Three signal channels:
 *
 *   1. Same-origin URL navigation (rare in practice — partner pages are
 *      almost always cross-origin) → iframe.contentWindow.location.href.
 *      Cross-origin throws SecurityError; we swallow.
 *   2. iframe `load` event count → coarse engagement signal (ms-to-load,
 *      navigation count). Always available, no origin restrictions.
 *   3. window `message` events from the iframe → the only cross-origin
 *      signal channel that lets us claim success without a webhook.
 *      Filter by `event.source === iframeRef.contentWindow` so messages
 *      from siblings/parents don't trigger.
 *
 * Telemetry events emitted (all carry the cart's item_id when present):
 *
 *   iframe_load           {item_id, dwell_since_open_ms, nav_count}
 *   iframe_focus / blur   (best-effort — focus events from cross-origin
 *                          iframes only fire when the user *first* clicks
 *                          into the iframe; subsequent focus moves stay
 *                          inside the iframe and are invisible to us)
 *   iframe_message        {item_id, type}
 *   iframe_success        {item_id, source: 'url'|'message', confirmation_code?}
 *   iframe_cancel         {item_id, source: 'url'|'message'}
 *   iframe_abandoned      {item_id, dwell_ms} — fired on unmount when no
 *                          success/cancel was seen
 */
import { useEffect, useMemo, useRef } from 'react';
import { emit as emitTelemetry } from '../utils/telemetry';
import logger from '../utils/logger';
import type { DetectionPatterns } from '../context/CheckoutContext';

interface UseIframeInspectorArgs {
  /** Iframe DOM ref (the one rendering the partner page). */
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  /** Whether the iframe is currently mounted/visible. */
  active: boolean;
  /** Provider rules from cart.detection_patterns. */
  patterns: DetectionPatterns | undefined;
  /** Optional cart item id — included in every telemetry event for join. */
  itemId?: string;
  /** Session id stamped onto telemetry. */
  sessionId?: string;
  /** Fired when success is detected. Carries the URL or message that triggered. */
  onSuccess?: (info: {
    source: 'url' | 'message';
    confirmation_code?: string;
    raw?: unknown;
  }) => void;
  /** Fired when cancel is detected. */
  onCancel?: (info: { source: 'url' | 'message' }) => void;
}

const DEFAULT_PATTERNS: Required<DetectionPatterns> = {
  success_patterns: [
    'confirmation',
    'success',
    'thank-you',
    'complete',
    'booked',
  ],
  checkout_patterns: ['checkout', 'payment', 'book'],
  cancel_patterns: ['cancel', 'abort'],
  confirmation_code_params: ['confirmation', 'code', 'booking_id', 'order_id'],
  success_message_types: [],
};

function matchesAny(haystack: string, needles: string[] | undefined): boolean {
  if (!needles || needles.length === 0) return false;
  const lower = haystack.toLowerCase();
  for (const n of needles) {
    if (n && lower.includes(n.toLowerCase())) return true;
  }
  return false;
}

function extractConfirmationCode(
  url: string,
  paramNames: string[] | undefined,
): string | undefined {
  if (!paramNames || paramNames.length === 0) return undefined;
  try {
    const params = new URL(url).searchParams;
    for (const k of paramNames) {
      const v = params.get(k);
      if (v) return v;
    }
  } catch {
    /* malformed url */
  }
  return undefined;
}

export function useIframeInspector({
  iframeRef,
  active,
  patterns,
  itemId,
  sessionId,
  onSuccess,
  onCancel,
}: UseIframeInspectorArgs): void {
  // Resolve effective patterns once per render — falls back to _default
  // when the cart didn't carry per-provider rules.
  const effective = useMemo<Required<DetectionPatterns>>(() => {
    return {
      success_patterns:
        patterns?.success_patterns ?? DEFAULT_PATTERNS.success_patterns,
      checkout_patterns:
        patterns?.checkout_patterns ?? DEFAULT_PATTERNS.checkout_patterns,
      cancel_patterns:
        patterns?.cancel_patterns ?? DEFAULT_PATTERNS.cancel_patterns,
      confirmation_code_params:
        patterns?.confirmation_code_params ??
        DEFAULT_PATTERNS.confirmation_code_params,
      success_message_types:
        patterns?.success_message_types ??
        DEFAULT_PATTERNS.success_message_types,
    };
  }, [patterns]);

  // Track engagement timing + whether we've already fired terminal events
  // so we don't double-emit and so the unmount handler can decide whether
  // to log abandonment.
  const openedAtRef = useRef<number>(0);
  const navCountRef = useRef<number>(0);
  const terminalRef = useRef<'success' | 'cancel' | null>(null);

  useEffect(() => {
    if (!active) return;
    openedAtRef.current = Date.now();
    navCountRef.current = 0;
    terminalRef.current = null;

    const iframe = iframeRef.current;

    function dwellMs() {
      return Date.now() - openedAtRef.current;
    }

    // -- URL detection (same-origin only — Securitypolicy blocks reads
    // -- across origins, which is fine; we silently fall through).
    function tryReadUrl(): string | undefined {
      try {
        return iframe?.contentWindow?.location?.href;
      } catch {
        return undefined;
      }
    }

    function maybeFireTerminalFromUrl(url: string) {
      if (terminalRef.current) return;
      if (matchesAny(url, effective.success_patterns)) {
        const code = extractConfirmationCode(
          url,
          effective.confirmation_code_params,
        );
        terminalRef.current = 'success';
        emitTelemetry('iframe_success', {
          sessionId,
          props: { item_id: itemId, source: 'url', confirmation_code: code },
        });
        onSuccess?.({ source: 'url', confirmation_code: code, raw: url });
        return;
      }
      if (matchesAny(url, effective.cancel_patterns)) {
        terminalRef.current = 'cancel';
        emitTelemetry('iframe_cancel', {
          sessionId,
          props: { item_id: itemId, source: 'url' },
        });
        onCancel?.({ source: 'url' });
      }
    }

    // -- Load handler: fires on each navigation within the iframe.
    function onLoad() {
      navCountRef.current += 1;
      emitTelemetry('iframe_load', {
        sessionId,
        props: {
          item_id: itemId,
          dwell_since_open_ms: dwellMs(),
          nav_count: navCountRef.current,
        },
      });
      const url = tryReadUrl();
      if (url) maybeFireTerminalFromUrl(url);
    }

    // -- Focus / blur — coarse but useful for engagement scoring.
    function onFocus() {
      // Cross-origin focus events fire when user clicks into the iframe.
      emitTelemetry('iframe_focus', {
        sessionId,
        props: { item_id: itemId, dwell_ms: dwellMs() },
      });
    }
    function onBlur() {
      emitTelemetry('iframe_blur', {
        sessionId,
        props: { item_id: itemId, dwell_ms: dwellMs() },
      });
    }

    // -- postMessage from iframe (cooperating providers). We can't trust
    // -- event.origin to identify the iframe, so we filter by source ===
    // -- iframe.contentWindow. event.data shape per provider; we match
    // -- type strings against success_message_types.
    function onMessage(ev: MessageEvent) {
      if (!iframe || ev.source !== iframe.contentWindow) return;
      const data = ev.data as Record<string, unknown> | undefined;
      const type = typeof data?.type === 'string' ? (data.type as string) : '';
      if (type) {
        emitTelemetry('iframe_message', {
          sessionId,
          props: { item_id: itemId, type },
        });
      }
      if (terminalRef.current) return;
      if (type && effective.success_message_types.some(t => t === type)) {
        terminalRef.current = 'success';
        const code =
          typeof data?.confirmation_code === 'string'
            ? (data.confirmation_code as string)
            : typeof data?.reservationId === 'string'
              ? (data.reservationId as string)
              : undefined;
        emitTelemetry('iframe_success', {
          sessionId,
          props: {
            item_id: itemId,
            source: 'message',
            confirmation_code: code,
          },
        });
        onSuccess?.({ source: 'message', confirmation_code: code, raw: data });
        return;
      }
      // Allow providers to signal cancel via a typed message too — use
      // the same cancel_patterns list, applied to the type string.
      if (type && matchesAny(type, effective.cancel_patterns)) {
        terminalRef.current = 'cancel';
        emitTelemetry('iframe_cancel', {
          sessionId,
          props: { item_id: itemId, source: 'message' },
        });
        onCancel?.({ source: 'message' });
      }
    }

    if (iframe) {
      iframe.addEventListener('load', onLoad);
      // Note: focus/blur on iframe element ≠ focus inside iframe content.
      // The window-level focus events approximate "user clicked into the
      // iframe" via blur on parent window; same-origin iframes get a
      // proper contentWindow.focus event but we can't access cross-origin.
      iframe.addEventListener('focus', onFocus);
      iframe.addEventListener('blur', onBlur);
    }
    window.addEventListener('message', onMessage);

    // First-load probe — some browsers fire `load` BEFORE we attach the
    // listener. If the iframe already shows a URL, run the URL check once.
    const firstUrl = tryReadUrl();
    if (firstUrl) maybeFireTerminalFromUrl(firstUrl);

    return () => {
      if (iframe) {
        iframe.removeEventListener('load', onLoad);
        iframe.removeEventListener('focus', onFocus);
        iframe.removeEventListener('blur', onBlur);
      }
      window.removeEventListener('message', onMessage);

      // Abandonment = unmount with no terminal signal seen and a non-
      // trivial dwell. <2s usually means the visitor changed tabs by
      // accident; >2s is a meaningful "they were trying to book and gave up".
      if (!terminalRef.current && dwellMs() > 2000) {
        emitTelemetry('iframe_abandoned', {
          sessionId,
          props: {
            item_id: itemId,
            dwell_ms: dwellMs(),
            nav_count: navCountRef.current,
          },
        });
        logger.info('[iframe-inspector] abandoned', {
          item_id: itemId,
          dwell_ms: dwellMs(),
        });
      }
    };
  }, [active, effective, iframeRef, itemId, sessionId, onSuccess, onCancel]);
}

export type { DetectionPatterns };
